import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { addMonths, isSameDay, isBefore, isWithinInterval } from "date-fns";
import { fr } from "date-fns/locale";
import { Typography, Paper, Box } from "@mui/material";
import { EventAvailable } from "@mui/icons-material";
import CalendarLegend from "./CalendarLegend";
import {
  weekdayKeys,
  formatLocalDateKey,
  normalizeDate,
} from "./calendarUtils";
import "./BookingCalendar.css";

const BookingCalendar = ({
  homePage = false,
  unavailableDates = [],
  bookedDates = [],
  availableWeekdays = {},
  onDateChange,
  highlightedStartDate,
  highlightedEndDate,
}) => {
  const [dateRange, setDateRange] = useState([null, null]);
  const [startDate, endDate] = dateRange;

  // Clean way to reset the calendar when highlighted dates change
  useEffect(() => {
    if (highlightedStartDate === null && highlightedEndDate === null) {
      setDateRange([null, null]);
    } else if (highlightedStartDate && highlightedEndDate) {
      setDateRange([highlightedStartDate, highlightedEndDate]);
    }
  }, [highlightedStartDate, highlightedEndDate]);

  const isDateAvailable = (date) => {
    if (!date) return false;

    const key = formatLocalDateKey(date);
    const weekday = weekdayKeys[date.getDay()];
    const isAllowedWeekday = availableWeekdays[weekday];
    const isUnavailable = unavailableDates.includes(key);
    const isBooked = bookedDates.includes(key);
    const isPast = isBefore(date, normalizeDate(new Date()));

    return !isPast && isAllowedWeekday && !isUnavailable && !isBooked;
  };

  // Check if a date is within the selected range
  const isDateInRange = (date) => {
    if (!startDate || !date) return false;
    if (!endDate) return isSameDay(date, startDate);

    return isWithinInterval(date, { start: startDate, end: endDate });
  };

  const filterAvailableDates = (date) => {
    return isDateAvailable(date);
  };

  const handleDateSelection = (dates) => {
    const [start, end] = dates;
    setDateRange(dates);

    // If both dates are selected, call the parent handler
    if (start && end) {
      const startKey = start ? formatLocalDateKey(start) : null;
      const endKey = end ? formatLocalDateKey(end) : null;
      onDateChange(start, end, startKey, endKey);
    }
  };

  const renderDayContents = (day, date) => {
    const dateKey = formatLocalDateKey(date);
    const isSelected = isDateInRange(date);
    const isBooked = bookedDates.includes(dateKey);
    let className = "custom-day ";

    if (isDateAvailable(date)) {
      className += "available-day";
    } else if (isBooked) {
      className += "booked-day";
    } else {
      className += "unavailable-day";
    }

    if (isSelected) {
      className += " selected-day";
    }

    return <div className={className}>{day}</div>;
  };

  return (
    <Paper
      elevation={homePage ? 0 : 3}
      className={!homePage && "calendar-paper"}
    >
      <Typography variant="h5" className="section-title">
        <EventAvailable className="section-icon" />
        Disponibilités
      </Typography>

      <Typography variant="body2" className="calendar-instructions">
        Sélectionnez une date de début et une date de fin pour votre réservation
      </Typography>

      <Box className="modern-calendar-container">
        <DatePicker
          selected={startDate}
          onChange={handleDateSelection}
          startDate={startDate}
          endDate={endDate}
          selectsRange
          inline
          locale={fr}
          minDate={new Date()}
          maxDate={addMonths(new Date(), 3)}
          monthsShown={1}
          renderDayContents={renderDayContents}
          disabledKeyboardNavigation
          calendarClassName={`custom-datepicker`}
          formatWeekDay={(nameOfDay) =>
            nameOfDay.charAt(0).toUpperCase() + nameOfDay.slice(1, 3)
          }
          filterDate={filterAvailableDates}
        />

        <CalendarLegend isAdmin={false} />
      </Box>
    </Paper>
  );
};

BookingCalendar.propTypes = {
  unavailableDates: PropTypes.arrayOf(PropTypes.string),
  bookedDates: PropTypes.arrayOf(PropTypes.string),
  availableWeekdays: PropTypes.object,
  onDateChange: PropTypes.func.isRequired,
  highlightedStartDate: PropTypes.instanceOf(Date),
  highlightedEndDate: PropTypes.instanceOf(Date),
};

export default BookingCalendar;
